/**
 * The connection to the core: send requests, receive answers and events.
 *
 * This is the **only** place in the GUI that talks to the core. Windows never open sockets;
 * they use the {@link Client} given to them. The client:
 *
 * - numbers every request and resolves the matching promise when the answer with the same id
 *   comes back (or rejects it with the core's {@link ClientError});
 * - forwards events (`graph.changed`, `log.entry`...) to whoever subscribed;
 * - reconnects on its own when the connection drops, and tells subscribers through
 *   {@link Client.onOpen} so they can reload what they show; unless the core refused this page
 *   (a space it is not a member of): then it stops and says why ({@link Client.refused}).
 *
 * The transport is passed in, so tests can drive the client with a fake one.
 */
import { createSignal, type Accessor } from "solid-js";
import type { ErrorCode } from "@protocol/ErrorCode";
import type { Event } from "@protocol/Event";
import type { Request } from "@protocol/Request";
import type { Response } from "@protocol/Response";
import type { ServerFrame } from "@protocol/ServerFrame";
import { createKeepalive, KEEPALIVE_MS } from "./keepalive";

/** Something able to carry text frames to and from the core (a WebSocket, or a fake in tests). */
export interface Transport {
  /** Opens the connection, or opens it again after it closed. */
  connect(): void;
  /** Sends one text frame. Returns `false` if the connection is not open. */
  send(text: string): boolean;
  /** Stops for good. No reconnection happens after this. */
  close(): void;
  /** Called when the connection is open. */
  onOpen(handler: () => void): void;
  /**
   * Called when the connection closed or failed to open. `refused` is set when the other side
   * turned this page away for good (it is not let in): the reason, for people.
   */
  onClose(handler: (refused?: string) => void): void;
  /** Called for every text frame received. */
  onMessage(handler: (text: string) => void): void;
}

/** Where the connection stands (`refused`: turned away, see {@link Client.refused}). */
export type Status = "connecting" | "open" | "closed" | "refused";

/** The WebSocket close code of a page that may not enter (`host_server::CLOSE_REFUSED`). */
export const CLOSE_REFUSED = 4403;

/**
 * Why a request failed: an error code sent by the core, or one of two codes produced by the
 * client itself (`disconnected` when the connection dropped before the answer came, and
 * `timeout` when no answer came in time).
 */
export interface ClientError {
  /** Machine readable reason. */
  code: ErrorCode | "disconnected";
  /** Explanation meant for people. */
  message: string;
}

/** `true` if `value` is a {@link ClientError} (useful in `catch` blocks). */
export function isClientError(value: unknown): value is ClientError {
  return typeof value === "object" && value !== null && "code" in value && "message" in value;
}

/** The API windows use to talk to the core. */
export interface Client {
  /** Reactive connection status. */
  status: Accessor<Status>;
  /** Why the core turned this page away (`null` while it is not refused). */
  refused: Accessor<string | null>;
  /** Sends a request and resolves with the answer, or rejects with a {@link ClientError}. */
  request(request: Request): Promise<Response>;
  /** Subscribes to events. Returns a function that unsubscribes. */
  onEvent(handler: (event: Event) => void): () => void;
  /** Called every time the connection opens (first time and after each reconnection). */
  onOpen(handler: () => void): () => void;
  /** Closes the connection for good. */
  close(): void;
}

/**
 * Requests that may take longer than the usual timeout, with their own limit (ms). Mirrors
 * `Request::time_limit` in the core (plus a little, so the core's own timeout answers first).
 */
export const SLOW_REQUESTS: Partial<Record<Request["method"], number>> = {
  "analyze.scan": 125_000,
  "analyze.scan_files": 125_000,
  // Fetching a repository (up to five minutes) and then scanning it.
  "repos.scan": 485_000,
  // Checking a plugin compiles it (a few seconds for a big one).
  "plugins.install": 65_000,
  // A plugin call runs the plugin a few times, sending its requests in between (the core gives up after 120 s).
  "plugins.call": 125_000,
  "plugins.command": 125_000,
  "plugins.view": 125_000,
  "plugins.action": 125_000,
  // An AI can think for minutes on a big graph (the core gives up after 300 s).
  "ai.suggest": 305_000,
  "ai.test": 305_000,
};

/** Tuning knobs, mostly for tests. */
export interface ClientOptions {
  /** How long to wait for an answer before rejecting with `timeout` (ms). */
  timeoutMs?: number;
  /** How long to wait before reconnecting after the connection dropped (ms). */
  retryMs?: number;
}

/** A request waiting for its answer. */
interface Waiting {
  resolve: (response: Response) => void;
  reject: (error: ClientError) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** Creates a client on top of `transport` and starts connecting. */
export function createClient(transport: Transport, options: ClientOptions = {}): Client {
  const timeoutMs = options.timeoutMs ?? 10_000;
  const retryMs = options.retryMs ?? 1_000;
  const [status, setStatus] = createSignal<Status>("connecting");
  const [refused, setRefused] = createSignal<string | null>(null);
  const waiting = new Map<number, Waiting>();
  const eventHandlers = new Set<(event: Event) => void>();
  const openHandlers = new Set<() => void>();
  let nextId = 1;
  /** The refusals of {@link notANumber} already told to the core's log (each once). */
  const toldBad = new Set<string>();
  let closedForGood = false;

  transport.onOpen(() => {
    setStatus("open");
    for (const handler of [...openHandlers]) handler();
  });

  transport.onClose((why) => {
    // Turned away: trying again would be turned away again, so stop and say why.
    if (why !== undefined) {
      closedForGood = true;
      setRefused(why);
    }
    setStatus(why !== undefined ? "refused" : closedForGood ? "closed" : "connecting");
    for (const [id, entry] of waiting) {
      clearTimeout(entry.timer);
      entry.reject({ code: "disconnected", message: "the connection to the core was lost" });
      waiting.delete(id);
    }
    if (!closedForGood) setTimeout(() => !closedForGood && transport.connect(), retryMs);
  });

  transport.onMessage((text) => {
    let frame: ServerFrame;
    try {
      frame = JSON.parse(text) as ServerFrame;
    } catch {
      return; // not a protocol frame; nothing sensible to do with it
    }
    if (frame.kind === "event") {
      for (const handler of [...eventHandlers]) handler(frame.event);
      return;
    }
    const entry = waiting.get(frame.id);
    if (!entry) return; // an answer to nothing we sent (or that already timed out)
    waiting.delete(frame.id);
    clearTimeout(entry.timer);
    if (frame.result.status === "ok") entry.resolve(frame.result.response);
    else entry.reject(frame.result.error);
  });

  transport.connect();

  return {
    status,
    refused,
    request(request) {
      return new Promise<Response>((resolve, reject) => {
        // A number JSON cannot write (`NaN`, an infinity) would reach the core as `null` and
        // be refused there: refused here instead, saying where, and told to the core's log once.
        const bad = notANumber("params" in request ? request.params : undefined);
        if (bad !== null) {
          const message = `${request.method}: ${bad || "params"} is not a number`;
          if (request.method !== "log.submit" && !toldBad.has(message)) {
            toldBad.add(message);
            transport.send(JSON.stringify({ id: nextId++, method: "log.submit", params: { level: "warn", source: "ui:client", message: `not sent: ${message}` } }));
          }
          reject({ code: "invalid_request", message });
          return;
        }
        const id = nextId++;
        const limit = Math.max(timeoutMs, SLOW_REQUESTS[request.method] ?? 0);
        const timer = setTimeout(() => {
          waiting.delete(id);
          reject({ code: "timeout", message: `no answer to ${request.method} within ${limit} ms` });
        }, limit);
        waiting.set(id, { resolve, reject, timer });
        if (!transport.send(JSON.stringify({ id, ...request }))) {
          waiting.delete(id);
          clearTimeout(timer);
          reject({ code: "disconnected", message: "not connected to the core" });
        }
      });
    },
    onEvent(handler) {
      eventHandlers.add(handler);
      return () => eventHandlers.delete(handler);
    },
    onOpen(handler) {
      openHandlers.add(handler);
      return () => openHandlers.delete(handler);
    },
    close() {
      closedForGood = true;
      transport.close();
    },
  };
}

/**
 * Where `value` holds a number JSON cannot write (`NaN`, an infinity), as a path such as
 * `cursor.anchor.dx` (`""`: `value` itself), or `null` when it holds none.
 */
export function notANumber(value: unknown, path = ""): string | null {
  if (typeof value === "number") return Number.isFinite(value) ? null : path;
  if (!value || typeof value !== "object") return null;
  for (const [key, inner] of Object.entries(value)) {
    const found = notANumber(inner, path ? `${path}.${key}` : key);
    if (found !== null) return found;
  }
  return null;
}

/** Thrown when the GUI cannot get in: the message says why, for people. */
export class SpaceRefusal extends Error {}

/**
 * A {@link Transport} over a browser WebSocket. `url` may be worked out before each connection
 * (a space page buys a one-time ticket with its session first); if that throws a
 * {@link SpaceRefusal}, the page is refused with its message (another error is retried). A close with {@link CLOSE_REFUSED} is a refusal too. While open it pings
 * the core now and then and drops a socket that stopped answering (`keepalive.ts`), so it
 * reconnects instead of hanging on a dead one.
 */
export function websocketTransport(url: string | (() => Promise<string>)): Transport {
  let socket: WebSocket | null = null;
  let stopped = false;
  let open: () => void = () => {};
  let close: (refused?: string) => void = () => {};
  let message: (text: string) => void = () => {};
  /** The running keepalive timer, while a socket is open. */
  let beat: ReturnType<typeof setInterval> | undefined;
  /** Opens a socket to `address`. */
  const start = (address: string) => {
    if (stopped) return;
    const current = new WebSocket(address);
    socket = current;
    /** This socket ended: stop its keepalive and say so, once. */
    const ended = (refused?: string) => {
      clearInterval(beat);
      current.onclose = current.onmessage = current.onopen = null;
      close(refused);
    };
    const keepalive = createKeepalive(
      (frame) => current.readyState === WebSocket.OPEN && current.send(frame),
      () => {
        // Dead though it looks open: give up on it now rather than wait for the browser.
        current.close();
        ended();
      },
    );
    current.onopen = () => {
      clearInterval(beat);
      beat = setInterval(() => keepalive.tick(), KEEPALIVE_MS);
      open();
    };
    current.onclose = (event) => ended(event.code === CLOSE_REFUSED ? event.reason || "this page may not enter here" : undefined);
    current.onmessage = (event) => {
      keepalive.heard();
      if (typeof event.data === "string") message(event.data);
    };
  };
  return {
    connect() {
      if (typeof url === "string") return start(url);
      // Turned away (signed out, no ticket): refused. Anything else (the server unreachable for a
      // moment): closed, and tried again like a dropped socket.
      url().then(start, (error: unknown) => close(error instanceof SpaceRefusal ? error.message : undefined));
    },
    send(text) {
      if (socket?.readyState !== WebSocket.OPEN) return false;
      socket.send(text);
      return true;
    },
    close() {
      stopped = true;
      clearInterval(beat);
      socket?.close();
    },
    onOpen(handler) {
      open = handler;
    },
    onClose(handler) {
      close = handler;
    },
    onMessage(handler) {
      message = handler;
    },
  };
}

/** The space a page address opens (`/s/<space>`), or `null` for the server's own core. */
export function spaceOf(pathname: string): string | null {
  return /^\/s\/([a-z0-9-]+)\/?$/.exec(pathname)?.[1] ?? null;
}
